import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createScan } from '../api';

interface GenerateScanModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export function GenerateScanModal({ open, onClose, onSubmitted }: GenerateScanModalProps) {
  const [scanId, setScanId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setScanId('');
      setError(null);
      setSubmitting(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = scanId.trim();
    if (!trimmed) {
      setError('Scan ID is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createScan(trimmed);
      onSubmitted();
      onClose();
      // Send the user to the video Live page so they immediately see
      // their freshly-submitted job.
      navigate('/video/live');
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to submit');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-900/30 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-card-hover border border-primary-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-primary-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-900">Generate Scan</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-primary-400 hover:text-primary-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 leading-snug">
            <span className="font-semibold">Heads up:</span> only submit scans whose merch images
            are <span className="font-semibold">published</span> in UVCamp. The scan must have all four
            of <code className="font-mono text-amber-900">center_front</code>,{' '}
            <code className="font-mono text-amber-900">center_rear</code>,{' '}
            <code className="font-mono text-amber-900">center_left</code> and{' '}
            <code className="font-mono text-amber-900">center_right</code>.
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-primary-800 mb-1">Scan ID</span>
            <input
              ref={inputRef}
              value={scanId}
              onChange={(e) => setScanId(e.target.value)}
              placeholder="e.g. cc47dfec-04ba-4cff-9bcb-196c1b1af4b9"
              disabled={submitting}
              className="w-full px-3 py-2 rounded-lg border border-primary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm font-mono disabled:opacity-60"
            />
          </label>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-gradient shadow-card hover:shadow-card-hover hover:opacity-95 transition-all disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
